<template>
  <div class="request-body-tree">
    <details v-if="isExpandable" class="request-body-tree__details" :open="openByDefault">
      <summary class="request-body-tree__summary">
        <span v-if="label !== undefined" class="request-body-tree__key">{{ label }}:</span>
        <span class="request-body-tree__group">{{ summaryLabel }}</span>
      </summary>

      <div class="request-body-tree__children">
        <RequestBodyTree
          v-for="(entry, index) in entries"
          :key="`${depth}-${entry.key}-${index}`"
          :label="entry.key"
          :value="entry.value"
          :depth="depth + 1"
          :open-by-default="false"
        />
      </div>
    </details>

    <div v-else class="request-body-tree__line">
      <span v-if="label !== undefined" class="request-body-tree__key">{{ label }}:</span>
      <span class="request-body-tree__value" :class="`request-body-tree__value--${primitiveClass}`">
        {{ formattedPrimitiveValue }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

defineOptions({
  name: 'RequestBodyTree',
});

const props = withDefaults(
  defineProps<{
    value: unknown;
    label?: string;
    depth?: number;
    openByDefault?: boolean;
  }>(),
  {
    label: undefined,
    depth: 0,
    openByDefault: false,
  }
);

interface TreeEntry {
  key: string;
  value: unknown;
}

const isExpandable = computed(() => Array.isArray(props.value) || isPlainObject(props.value));

const entries = computed<TreeEntry[]>(() => {
  if (Array.isArray(props.value)) {
    return props.value.map((value, index) => ({ key: String(index), value }));
  }

  if (isPlainObject(props.value)) {
    return Object.entries(props.value).map(([key, value]) => ({ key, value }));
  }

  return [];
});

const summaryLabel = computed(() => {
  if (Array.isArray(props.value)) {
    return `Array(${props.value.length})`;
  }

  if (isPlainObject(props.value)) {
    return `Object(${Object.keys(props.value).length})`;
  }

  return '';
});

const formattedPrimitiveValue = computed(() => {
  if (typeof props.value === 'string') return `"${props.value}"`;
  if (props.value === null) return 'null';
  if (props.value === undefined) return 'undefined';
  return String(props.value);
});

const primitiveClass = computed<'string' | 'number' | 'boolean' | 'null' | 'undefined' | 'default'>(
  () => {
    if (typeof props.value === 'string') return 'string';
    if (typeof props.value === 'number') return 'number';
    if (typeof props.value === 'boolean') return 'boolean';
    if (props.value === null) return 'null';
    if (props.value === undefined) return 'undefined';
    return 'default';
  }
);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
</script>

<style scoped>
.request-body-tree {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 13px;
  color: var(--ion-text-color, #1f2937);
}

.request-body-tree__details {
  margin: 2px 0;
}

.request-body-tree__summary {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  cursor: pointer;
  line-height: 1.4;
  user-select: none;
}

.request-body-tree__children {
  margin-left: 12px;
  padding-left: 10px;
  border-left: 1px solid var(--ion-input-border-color, rgba(0, 0, 0, 0.2));
}

.request-body-tree__line {
  display: flex;
  align-items: baseline;
  gap: 8px;
  line-height: 1.45;
  margin: 3px 0;
}

.request-body-tree__key {
  color: var(--ion-text-color, #111827);
  font-weight: 600;
}

.request-body-tree__group {
  color: var(--ion-color-medium-shade, #4b5563);
  font-weight: 600;
}

.request-body-tree__value--string {
  color: #0f4aa6;
}

.request-body-tree__value--number {
  color: #5b21b6;
}

.request-body-tree__value--boolean {
  color: #0f8a43;
}

.request-body-tree__value--null,
.request-body-tree__value--undefined {
  color: var(--ion-color-medium, #6b7280);
  font-style: italic;
}
</style>
